import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Vault as VaultIcon, Loader2, Home, Users, ArrowLeft, Zap } from "lucide-react";
import { api } from "../lib/api";
import AuthPage from "./AuthPage";

const PROMO_BG = "https://images.unsplash.com/photo-1536138746221-2c489206ff6c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGx1eHVyeSUyMHRleHR1cmV8ZW58MHx8fGJsYWNrfDE3ODY4Mjk4MDh8MA&ixlib=rb-4.1.0&q=85";

export default function PromoLanding({ code }) {
  const [promo, setPromo] = useState(null);
  const [invalid, setInvalid] = useState(false);
  const [choice, setChoice] = useState(null); // null | "join" | "create"

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/referral/resolve/${code}`);
        setPromo(data);
        // Always record the promotional referral on the eventual signup.
        localStorage.setItem("fv_ref", code.toUpperCase());
      } catch { setInvalid(true); }
    })();
  }, [code]);

  const pickJoin = () => {
    if (promo?.family_code) localStorage.setItem("fv_invite", promo.family_code);
    setChoice("join");
  };
  const pickCreate = () => {
    localStorage.removeItem("fv_invite");
    setChoice("create");
  };

  if (invalid) {
    return (
      <div className="min-h-screen w-full grain grid place-items-center px-7 text-center" style={{ background: "var(--bg-base)" }} data-testid="promo-invalid">
        <div>
          <h1 className="font-serif-display text-3xl">Invite not found</h1>
          <p className="mt-3 text-sm" style={{ color: "var(--text-secondary)" }}>
            This invite link is invalid or has expired. Ask your friend for a new one.
          </p>
        </div>
      </div>
    );
  }

  if (!promo) return <div className="min-h-screen w-full grid place-items-center" style={{ background: "var(--bg-base)" }}><Loader2 className="animate-spin gold-text" /></div>;

  const heroName = choice === "join" && promo.family_name ? promo.family_name : "The Family Vault";

  return (
    <div className="min-h-screen w-full grain relative" style={{ background: "var(--bg-base)" }} data-testid="promo-landing">
      <div className="lg:grid lg:grid-cols-2 min-h-screen">
        {/* Brand / hero panel — desktop */}
        <div className="hidden lg:block relative overflow-hidden">
          <img src={PROMO_BG} alt="" className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(120deg, rgba(11,11,18,0.92), rgba(11,11,18,0.6))" }} />
          <div className="relative z-10 h-full flex flex-col justify-between p-14">
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-xl grid place-items-center gold-border" style={{ background: "rgba(20,20,31,0.6)" }}>
                <VaultIcon size={22} className="gold-text" />
              </div>
              <span className="font-serif-display text-2xl">The Family Vault</span>
            </div>
            <div>
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest mb-3 px-3 py-1.5 rounded-full" style={{ background: "color-mix(in srgb, var(--accent) 16%, transparent)", color: "var(--text-secondary)" }}>
                <Zap size={13} className="gold-text" /> Personal invitation
              </div>
              <h2 className="font-serif-display text-6xl leading-[1.02] gold-text">{promo.referrer_name} invited you</h2>
              <p className="mt-5 text-base max-w-sm" style={{ color: "var(--text-secondary)" }}>
                Join your family's private vault — lineage, gatherings, memories and more.
              </p>
            </div>
            <div className="text-xs" style={{ color: "var(--text-muted)" }}>© {new Date().getFullYear()} The Family Vault</div>
          </div>
        </div>

        {/* Right column */}
        <div className="relative z-10 flex items-center justify-center px-6 py-14 min-h-screen">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="w-full max-w-md">
            <div className="h-14 w-14 rounded-2xl grid place-items-center gold-border mb-6"
              style={{ background: "var(--bg-elevated)", boxShadow: "0 0 24px var(--shell-glow)" }}>
              <Zap size={26} className="gold-text" />
            </div>

            {!choice && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="promo-choice-step">
                <h1 className="font-serif-display text-4xl sm:text-5xl leading-none" data-testid="promo-referrer-name">
                  <span className="gold-text">{promo.referrer_name}</span> invited you to The Family Vault
                </h1>
                <p className="mt-4 text-sm" style={{ color: "var(--text-secondary)" }}>
                  Get started in seconds — join {promo.family_name ? promo.family_name : "your friend's family"} or start your own family vault.
                </p>
                <div className="mt-8 space-y-4">
                  {promo.family_code && (
                    <motion.button whileTap={{ scale: 0.98 }} onClick={pickJoin} data-testid="promo-join-family-btn"
                      className="w-full card-surface rounded-2xl p-5 text-left flex items-start gap-4">
                      <div className="h-11 w-11 rounded-xl grid place-items-center gold-border shrink-0" style={{ background: "var(--bg-elevated)" }}>
                        <Users size={20} className="gold-text" />
                      </div>
                      <div>
                        <div className="font-semibold">Join {promo.family_name}</div>
                        <div className="text-xs mt-0.5" style={{ color: "var(--text-secondary)" }}>Become a member of {promo.referrer_name}'s family vault.</div>
                      </div>
                    </motion.button>
                  )}
                  <motion.button whileTap={{ scale: 0.98 }} onClick={pickCreate} data-testid="promo-create-family-btn"
                    className="w-full card-surface rounded-2xl p-5 text-left flex items-start gap-4">
                    <div className="h-11 w-11 rounded-xl grid place-items-center gold-border shrink-0" style={{ background: "var(--bg-elevated)" }}>
                      <Home size={20} className="gold-text" />
                    </div>
                    <div>
                      <div className="font-semibold">Create your own family</div>
                      <div className="text-xs mt-0.5" style={{ color: "var(--text-secondary)" }}>Start a fresh private vault and become its first admin.</div>
                    </div>
                  </motion.button>
                </div>
              </motion.div>
            )}

            {choice && (
              <motion.div initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} data-testid="promo-register-step">
                <button onClick={() => setChoice(null)} className="mb-4 flex items-center gap-1 text-sm" style={{ color: "var(--text-secondary)" }} data-testid="promo-back-btn">
                  <ArrowLeft size={16} /> Back
                </button>
                <h1 className="font-serif-display text-4xl sm:text-5xl leading-none gold-text">{heroName}</h1>
                <p className="mt-3 text-sm" style={{ color: "var(--text-secondary)" }}>
                  {choice === "join"
                    ? `Create your account to join ${promo.family_name}.`
                    : "Create your account, then set up your new family vault."}
                </p>
                <div className="mt-8">
                  <AuthPage defaultMode="register" hideBranding />
                </div>
              </motion.div>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
}
